import { get, set, del } from 'idb-keyval';

/**
 * Serviço de Token com fallback para localStorage
 * Resolve problemas do IndexedDB no Safari/iOS
 */

const TOKEN_KEY = 'token';
const TOKEN_BACKUP_KEY = 'token_backup';

/**
 * Salva o token no IndexedDB e localStorage (backup)
 * @param {string} token - Token de autenticação
 * @returns {Promise<void>}
 */
export async function setToken(token) {
  try {
    // Salvar no IndexedDB
    await set(TOKEN_KEY, token);
    // Salvar backup no localStorage
    localStorage.setItem(TOKEN_BACKUP_KEY, token);
  } catch (error) {
    console.error('Error saving token to IndexedDB, using localStorage fallback:', error);
    // Se falhar no IndexedDB, pelo menos salvar no localStorage
    localStorage.setItem(TOKEN_BACKUP_KEY, token);
  }
}

/**
 * Busca o token do IndexedDB, com fallback para localStorage
 * @returns {Promise<string|null>}
 */
export async function getToken() {
  try {
    // Tentar buscar do IndexedDB primeiro
    const token = await get(TOKEN_KEY);

    if (token) {
      // Sincronizar com localStorage se houver token
      localStorage.setItem(TOKEN_BACKUP_KEY, token);
      return token;
    }

    // Fallback: buscar do localStorage (chave nova)
    const backupToken = localStorage.getItem(TOKEN_BACKUP_KEY);

    if (backupToken) {
      // Tentar restaurar no IndexedDB
      try {
        await set(TOKEN_KEY, backupToken);
      } catch (err) {
        console.warn('Could not restore token to IndexedDB:', err);
      }
      return backupToken;
    }

    // Fallback: buscar chave legada do localStorage (compatibilidade)
    const legacyToken = localStorage.getItem('token');

    if (legacyToken) {
      // Migrar para o novo formato
      try {
        await set(TOKEN_KEY, legacyToken);
        localStorage.setItem(TOKEN_BACKUP_KEY, legacyToken);
        // Remover chave legada após migração
        localStorage.removeItem('token');
      } catch (err) {
        console.warn('Could not migrate legacy token:', err);
      }
      return legacyToken;
    }

    return null;
  } catch (error) {
    console.error('Error getting token from IndexedDB, using localStorage fallback:', error);
    // Em caso de erro, usar localStorage (tentar ambas as chaves)
    return localStorage.getItem(TOKEN_BACKUP_KEY) || localStorage.getItem('token');
  }
}

/**
 * Remove o token do IndexedDB e localStorage
 * @returns {Promise<void>}
 */
export async function deleteToken() {
  try {
    await del(TOKEN_KEY);
  } catch (error) {
    console.error('Error deleting token from IndexedDB:', error);
  }

  // Sempre limpar o localStorage também
  localStorage.removeItem(TOKEN_BACKUP_KEY);
  // Limpar chave legada (compatibilidade com código antigo)
  localStorage.removeItem('token');
}

/**
 * Verifica se existe um token válido
 * @returns {Promise<boolean>}
 */
export async function hasToken() {
  const token = await getToken();
  return !!token;
}

export default {
  setToken,
  getToken,
  deleteToken,
  hasToken
};




